import { motion } from "framer-motion";
import { Users, Heart } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import type { BestyData } from "@/hooks/useWrappedStats";

interface BestiesSlideProps {
  besties: BestyData[];
}

const BestiesSlide = ({ besties }: BestiesSlideProps) => {
  if (besties.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center text-center px-6">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200 }}
          className="mb-6"
        >
          <Users className="w-16 h-16 text-secondary" />
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-2xl font-bold text-background mb-4"
        >
          Solo Explorer
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-background/70"
        >
          You've been enjoying art on your own terms!
        </motion.p>
      </div>
    );
  }

  // Reorder for podium display: [2nd, 1st, 3rd]
  const podiumOrder = besties.length >= 3
    ? [besties[1], besties[0], besties[2]]
    : besties.length === 2
    ? [besties[1], besties[0]]
    : [besties[0]];

  const podiumHeights = ["h-24", "h-32", "h-20"];
  const podiumDelays = [0.8, 0.4, 1.2];

  return (
    <div className="flex flex-col items-center justify-center text-center px-6 w-full max-w-md">
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 200 }}
        className="mb-4"
      >
        <Heart className="w-12 h-12 text-secondary fill-secondary" />
      </motion.div>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-2xl font-bold text-background mb-2"
      >
        Your Nimi Besties
      </motion.h2>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-background/70 mb-8"
      >
        The people you explored art with most
      </motion.p>

      {/* Podium */}
      <div className="flex items-end justify-center gap-4 w-full">
        {podiumOrder.map((besty, idx) => {
          if (!besty) return null;
          const position = besties.length >= 3 ? idx : idx === 0 && besties.length === 2 ? 0 : 1;
          const actualRank = besties.length >= 3 
            ? [2, 1, 3][idx] 
            : besties.length === 2 
            ? [2, 1][idx]
            : 1;

          return (
            <motion.div
              key={besty.user_id}
              className="flex flex-col items-center"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: podiumDelays[position], duration: 0.5 }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: podiumDelays[position] + 0.3, type: "spring" }}
              >
                <Avatar className={`${position === 1 ? "w-20 h-20" : "w-16 h-16"} border-2 border-secondary`}>
                  <AvatarImage src={besty.avatar_url || undefined} />
                  <AvatarFallback className="bg-secondary text-primary text-lg font-bold">
                    {besty.first_name?.[0] || "?"}
                  </AvatarFallback>
                </Avatar>
              </motion.div>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: podiumDelays[position] + 0.5 }}
                className="text-sm font-medium text-background mt-2 truncate max-w-[80px]"
              >
                {besty.first_name || "Friend"}
              </motion.p>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: podiumDelays[position] + 0.6 }}
                className="text-xs text-background/60"
              >
                {besty.shared_events} events
              </motion.p>

              <motion.div
                initial={{ scaleY: 0 }}
                animate={{ scaleY: 1 }}
                transition={{ delay: podiumDelays[position] + 0.2, duration: 0.4 }}
                style={{ originY: 1 }}
                className={`${podiumHeights[position]} w-20 mt-2 rounded-t-lg flex items-center justify-center ${
                  position === 1 ? "bg-secondary" : "bg-secondary/60"
                }`}
              >
                <span className="text-2xl font-bold text-primary">#{actualRank}</span>
              </motion.div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default BestiesSlide;
